import React, { useState, useEffect } from 'react';
import { 
  Inbox, Search, Filter, Download, RefreshCw, Eye, Mail, Phone, Building2,
  Calendar, Users, DollarSign, Clock, CheckCircle, XCircle, UserPlus,
  ChevronRight, ChevronDown, FileText, ExternalLink, AlertCircle, Loader2,
  MoreVertical, MessageSquare, UserCheck, X, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../utils/api';

// Status badge component
const StatusBadge = ({ status }) => {
  const styles = {
    'New': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'Read': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    'In Review': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    'Converted': 'bg-green-500/20 text-green-400 border-green-500/30',
    'Closed': 'bg-red-500/20 text-red-400 border-red-500/30',
  };
  
  const icons = {
    'New': <AlertCircle size={12} />,
    'Read': <Eye size={12} />,
    'In Review': <Clock size={12} />,
    'Converted': <UserCheck size={12} />,
    'Closed': <XCircle size={12} />,
  };
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles['New']}`}>
      {icons[status] || icons['New']}
      {status}
    </span>
  );
};

// Client type badge
const ClientTypeBadge = ({ type }) => {
  const colors = {
    'Corporate': 'text-blue-400',
    'Government': 'text-purple-400',
    'Private': 'text-[#D4AF37]',
    'NGO': 'text-green-400',
    'Other': 'text-gray-400',
  };
  
  return (
    <span className={`text-xs font-medium ${colors[type] || colors['Other']}`}>
      {type || 'Unknown'}
    </span>
  );
};

const AdminInquiriesPage = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  
  // Filters - default to 'all' to show everything
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientTypeFilter, setClientTypeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 20;
  
  // Google Sheets status
  const [gsheetsStatus, setGsheetsStatus] = useState(null);
  const [testingGsheets, setTestingGsheets] = useState(false);
  
  // Debug info for troubleshooting
  const [lastFetchInfo, setLastFetchInfo] = useState(null);

  useEffect(() => {
    fetchInquiries();
    fetchGsheetsStatus();
  }, [statusFilter, clientTypeFilter, dateFrom, dateTo, currentPage]); // searchQuery handled separately via handleSearch

  const fetchInquiries = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      console.log('[AdminInquiries] Fetching with params:', {
        status: statusFilter,
        client_type: clientTypeFilter,
        date_from: dateFrom,
        date_to: dateTo,
        search: searchQuery,
        skip: currentPage * pageSize,
        limit: pageSize
      });
      
      const response = await api.getAdminInquiries({
        status: statusFilter,
        client_type: clientTypeFilter,
        date_from: dateFrom,
        date_to: dateTo,
        search: searchQuery,
        skip: currentPage * pageSize,
        limit: pageSize
      });
      
      console.log('[AdminInquiries] Response received:', {
        total: response.data.total,
        returned: response.data.inquiries?.length,
        status_counts: response.data.status_counts
      });
      
      // Normalize inquiries to handle schema variations
      const normalizedInquiries = (response.data.inquiries || []).map(inq => ({
        ...inq,
        // Map old schema fields to new schema if needed
        full_name: inq.full_name || inq.name || 'N/A',
        created_at: inq.created_at || inq.submitted_at,
        inquiry_message: inq.inquiry_message || inq.description || '',
        phone_whatsapp: inq.phone_whatsapp || inq.phone || '',
        company_organization: inq.company_organization || inq.organization || '',
        // Normalize status to capitalized form
        status: normalizeStatus(inq.status)
      }));
      setInquiries(normalizedInquiries);
      setTotal(response.data.total || 0);
      setStatusCounts(response.data.status_counts || {});
      setLastFetchInfo({
        timestamp: new Date().toISOString(),
        apiReturned: response.data.inquiries?.length || 0,
        totalInDb: response.data.total || 0,
        error: null
      });
    } catch (error) {
      console.error('[AdminInquiries] Fetch error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to load inquiries';
      setFetchError(errorMsg);
      setLastFetchInfo({
        timestamp: new Date().toISOString(),
        apiReturned: 0,
        totalInDb: 0,
        error: errorMsg
      });
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  // Helper to normalize status values (handle lowercase from older records)
  const normalizeStatus = (status) => {
    if (!status) return 'New';
    const statusMap = {
      'new': 'New',
      'read': 'Read',
      'in review': 'In Review',
      'in_review': 'In Review',
      'converted': 'Converted',
      'closed': 'Closed'
    };
    return statusMap[status.toLowerCase()] || status;
  };

  const fetchGsheetsStatus = async () => {
    try {
      const response = await api.getGsheetsStatus();
      setGsheetsStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch GSheets status:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchInquiries();
  };

  const handleMarkRead = async (inquiryId) => {
    try {
      await api.markInquiryRead(inquiryId);
      toast.success('Marked as read');
      fetchInquiries();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleStatusChange = async (inquiryId, newStatus) => {
    try {
      await api.updateInquiry(inquiryId, { status: newStatus });
      toast.success('Status updated');
      fetchInquiries();
      if (selectedInquiry?.inquiry_id === inquiryId) {
        setSelectedInquiry({ ...selectedInquiry, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.exportInquiriesCSV({
        status: statusFilter,
        client_type: clientTypeFilter,
        date_from: dateFrom,
        date_to: dateTo
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inquiries_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const handleTestGsheets = async () => {
    setTestingGsheets(true);
    try {
      const response = await api.testGsheetsConnection();
      if (response.data.success) {
        toast.success('Google Sheets connection successful!');
      } else {
        toast.error(response.data.error || 'Connection failed');
      }
    } catch (error) {
      toast.error('Failed to test connection');
    } finally {
      setTestingGsheets(false);
    }
  };

  const openInquiryDetail = async (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowDetailDrawer(true);
    
    // Mark as read if new
    if (inquiry.status === 'New') {
      handleMarkRead(inquiry.inquiry_id);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-inquiries-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Inbox className="text-[#D4AF37]" />
            Public Inquiries
          </h1>
          <p className="text-white/60 text-sm">Manage consultation requests from the public website</p>
        </div>
        <div className="flex items-center gap-3">
          {gsheetsStatus?.enabled && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleTestGsheets}
              disabled={testingGsheets}
              className="border-white/20"
            >
              {testingGsheets ? <Loader2 className="animate-spin mr-2" size={14} /> : <ExternalLink size={14} className="mr-2" />}
              Test Google Sheets
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            className="border-white/20"
          >
            <Download size={14} className="mr-2" />
            Export CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchInquiries}
            className="border-white/20"
            data-testid="refresh-inquiries-btn"
          >
            <RefreshCw size={14} className="mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Debug Info Panel (Admin-only) */}
      {lastFetchInfo && (
        <div className="bg-[#0A0A0F] border border-white/10 rounded-lg p-3 text-xs" data-testid="fetch-debug-info">
          <div className="flex items-center justify-between">
            <span className="text-white/40">
              Last fetch: {new Date(lastFetchInfo.timestamp).toLocaleTimeString()} |
              API returned: <span className="text-white font-medium">{lastFetchInfo.apiReturned}</span> records |
              Total in DB: <span className="text-white font-medium">{lastFetchInfo.totalInDb}</span>
            </span>
            {lastFetchInfo.error && (
              <span className="text-red-400">Error: {lastFetchInfo.error}</span>
            )}
          </div>
        </div>
      )}

      {/* Status Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { status: 'New', icon: AlertCircle, color: 'text-blue-400', bg: 'bg-blue-500/10' },
          { status: 'Read', icon: Eye, color: 'text-gray-400', bg: 'bg-gray-500/10' },
          { status: 'In Review', icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
          { status: 'Converted', icon: UserCheck, color: 'text-green-400', bg: 'bg-green-500/10' },
          { status: 'Closed', icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10' },
        ].map(({ status, icon: Icon, color, bg }) => (
          <button
            key={status}
            onClick={() => { setStatusFilter(status); setCurrentPage(0); }}
            className={`p-4 rounded-lg border transition-all ${
              statusFilter === status 
                ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                : 'border-white/5 bg-[#0A0A0F] hover:border-white/20'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <Icon className={color} size={20} />
              <span className="text-2xl font-bold">{statusCounts[status] || 0}</span>
            </div>
            <p className="text-xs text-white/60">{status}</p>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-[#0A0A0F] border border-white/5 rounded-lg p-4">
        <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
            <Input
              placeholder="Search name, email, phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/5 border-white/10"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(0); }}
            className="bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm"
          >
            <option value="all">All Status</option>
            <option value="New">New</option>
            <option value="Read">Read</option>
            <option value="In Review">In Review</option>
            <option value="Converted">Converted</option>
            <option value="Closed">Closed</option>
          </select>
          
          <select
            value={clientTypeFilter}
            onChange={(e) => { setClientTypeFilter(e.target.value); setCurrentPage(0); }}
            className="bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm"
          >
            <option value="all">All Client Types</option>
            <option value="Corporate">Corporate</option>
            <option value="Government">Government</option>
            <option value="Private">Private</option>
            <option value="NGO">NGO</option>
            <option value="Other">Other</option>
          </select>
          
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            placeholder="From"
            className="w-40 bg-white/5 border-white/10"
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            placeholder="To"
            className="w-40 bg-white/5 border-white/10"
          />
          
          <Button type="submit" size="sm" className="bg-[#D4AF37] hover:bg-[#B8963A] text-black">
            <Filter size={14} className="mr-2" />
            Apply
          </Button>
          
          {(statusFilter !== 'all' || clientTypeFilter !== 'all' || dateFrom || dateTo || searchQuery) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setStatusFilter('all');
                setClientTypeFilter('all');
                setDateFrom('');
                setDateTo('');
                setSearchQuery('');
                setCurrentPage(0);
              }}
              className="text-white/60"
            >
              Clear Filters
            </Button>
          )}
        </form>
      </div>

      {/* Active Filters Indicator */}
      {(statusFilter !== 'all' || clientTypeFilter !== 'all' || dateFrom || dateTo || searchQuery) && (
        <div className="flex items-center gap-2 flex-wrap px-1 text-sm">
          <span className="text-white/40">Active filters:</span>
          {statusFilter !== 'all' && (
            <span className="px-2 py-1 bg-[#D4AF37]/20 text-[#D4AF37] rounded text-xs">
              Status: {statusFilter}
            </span>
          )}
          {clientTypeFilter !== 'all' && (
            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
              Type: {clientTypeFilter}
            </span>
          )}
          {searchQuery && (
            <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs">
              Search: "{searchQuery}"
            </span>
          )}
          {(dateFrom || dateTo) && (
            <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
              Date: {dateFrom || '...'} → {dateTo || '...'}
            </span>
          )}
        </div>
      )}

      {/* Inquiries Table */}
      <div className="bg-[#0A0A0F] border border-white/5 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12" data-testid="inquiries-loading">
            <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
          </div>
        ) : fetchError ? (
          <div className="text-center py-12" data-testid="inquiries-error">
            <AlertCircle className="mx-auto mb-4 text-red-400" size={48} />
            <p className="text-red-400 font-medium mb-2">Failed to load inquiries</p>
            <p className="text-white/40 text-sm mb-4">{fetchError}</p>
            <Button 
              onClick={fetchInquiries} 
              variant="outline" 
              size="sm"
              className="border-white/20"
            >
              <RefreshCw size={14} className="mr-2" />
              Try Again
            </Button>
          </div>
        ) : inquiries.length === 0 ? (
          <div className="text-center py-12 text-white/60" data-testid="inquiries-empty">
            <Inbox className="mx-auto mb-4 opacity-40" size={48} />
            <p className="font-medium mb-1">
              {lastFetchInfo?.apiReturned === 0 && lastFetchInfo?.totalInDb === 0 
                ? '0 records in database' 
                : 'No inquiries match current filters'}
            </p>
            <p className="text-sm text-white/40 mb-4">
              {statusFilter !== 'all' || clientTypeFilter !== 'all' || searchQuery || dateFrom || dateTo
                ? 'Try adjusting your filters or search query' 
                : 'New inquiries from the public contact form will appear here'}
            </p>
            {(statusFilter !== 'all' || clientTypeFilter !== 'all' || searchQuery || dateFrom || dateTo) && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setStatusFilter('all');
                  setClientTypeFilter('all');
                  setSearchQuery('');
                  setDateFrom('');
                  setDateTo('');
                  setCurrentPage(0);
                }}
                className="border-white/20"
              >
                Clear All Filters
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-white/60">Date</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-white/60">Contact</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-white/60">Client Type</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-white/60">Event</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-white/60">Budget</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-white/60">Status</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-white/60">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {inquiries.map((inquiry) => (
                  <tr 
                    key={inquiry.inquiry_id} 
                    className={`hover:bg-white/5 cursor-pointer transition-colors ${
                      inquiry.status === 'New' ? 'bg-blue-500/5' : ''
                    }`}
                    onClick={() => openInquiryDetail(inquiry)}
                  >
                    <td className="px-4 py-3">
                      <div className="text-sm">{new Date(inquiry.created_at).toLocaleDateString()}</div>
                      <div className="text-xs text-white/40">{new Date(inquiry.created_at).toLocaleTimeString()}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-sm">{inquiry.full_name || 'N/A'}</div>
                      <div className="text-xs text-white/60">{inquiry.email}</div>
                      {inquiry.phone_whatsapp && (
                        <div className="text-xs text-white/40">{inquiry.phone_whatsapp}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <ClientTypeBadge type={inquiry.client_type} />
                      {inquiry.company_organization && (
                        <div className="text-xs text-white/40 mt-1">{inquiry.company_organization}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">{inquiry.event_type || 'N/A'}</div>
                      <div className="text-xs text-white/40">{inquiry.event_date_or_range || 'Date TBD'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-[#D4AF37]">{inquiry.budget_range || 'N/A'}</div>
                      <div className="text-xs text-white/40">{inquiry.expected_guest_count_range || ''} guests</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StatusBadge status={inquiry.status} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); openInquiryDetail(inquiry); }}
                      >
                        <Eye size={16} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {total > pageSize && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
            <div className="text-sm text-white/60">
              Showing {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, total)} of {total}
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={currentPage === 0}
                onClick={() => setCurrentPage(p => p - 1)}
                className="border-white/20"
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={(currentPage + 1) * pageSize >= total}
                onClick={() => setCurrentPage(p => p + 1)}
                className="border-white/20"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      {showDetailDrawer && selectedInquiry && (
        <InquiryDetailDrawer
          inquiry={selectedInquiry}
          onClose={() => { setShowDetailDrawer(false); setSelectedInquiry(null); }}
          onStatusChange={handleStatusChange}
          onConvert={() => setShowConvertModal(true)}
          onRefresh={fetchInquiries}
        />
      )}

      {/* Convert Modal */}
      {showConvertModal && selectedInquiry && (
        <ConvertToClientModal
          inquiry={selectedInquiry}
          onClose={() => setShowConvertModal(false)}
          onSuccess={() => {
            setShowConvertModal(false);
            setShowDetailDrawer(false);
            fetchInquiries();
          }}
        />
      )}
    </div>
  );
};

// Inquiry Detail Drawer
const InquiryDetailDrawer = ({ inquiry, onClose, onStatusChange, onConvert, onRefresh }) => {
  const [internalNotes, setInternalNotes] = useState(inquiry.internal_notes || '');
  const [assignedTo, setAssignedTo] = useState(inquiry.assigned_to || '');
  const [saving, setSaving] = useState(false);

  const handleSaveNotes = async () => {
    setSaving(true);
    try {
      await api.updateInquiry(inquiry.inquiry_id, { 
        internal_notes: internalNotes,
        assigned_to: assignedTo
      });
      toast.success('Notes saved');
      onRefresh();
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex justify-end">
      <div className="w-full max-w-2xl bg-[#0A0A0F] border-l border-white/10 h-full overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#0A0A0F] border-b border-white/10 p-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium">Inquiry Details</h2>
            <p className="text-sm text-white/60">{inquiry.inquiry_id}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X size={20} />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* Status & Actions */}
          <div className="flex items-center justify-between">
            <StatusBadge status={inquiry.status} />
            <div className="flex gap-2">
              <select
                value={inquiry.status}
                onChange={(e) => onStatusChange(inquiry.inquiry_id, e.target.value)}
                className="bg-white/5 border border-white/10 rounded-md px-3 py-1 text-sm"
              >
                <option value="New">New</option>
                <option value="Read">Read</option>
                <option value="In Review">In Review</option>
                <option value="Converted">Converted</option>
                <option value="Closed">Closed</option>
              </select>
              {!inquiry.converted_to_client && (
                <Button
                  size="sm"
                  onClick={onConvert}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <UserPlus size={14} className="mr-1" />
                  Convert to Client
                </Button>
              )}
            </div>
          </div>

          {inquiry.converted_to_client && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-400">
                <CheckCircle size={18} />
                <span className="font-medium">Converted to Client</span>
              </div>
              <p className="text-sm text-white/60 mt-1">
                Client ID: {inquiry.converted_client_id}
              </p>
              <p className="text-xs text-white/40">
                Converted on: {inquiry.converted_at ? new Date(inquiry.converted_at).toLocaleString() : 'N/A'}
              </p>
            </div>
          )}

          {/* Contact Information */}
          <div className="bg-white/5 rounded-lg p-4">
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <Mail className="text-[#D4AF37]" size={16} />
              Contact Information
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-white/40">Full Name</p>
                <p className="font-medium">{inquiry.full_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-white/40">Email</p>
                <p className="font-medium">{inquiry.email || 'N/A'}</p>
              </div>
              <div>
                <p className="text-white/40">Phone/WhatsApp</p>
                <p className="font-medium">{inquiry.phone_whatsapp || 'N/A'}</p>
              </div>
              <div>
                <p className="text-white/40">Company/Organization</p>
                <p className="font-medium">{inquiry.company_organization || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Client Classification */}
          <div className="bg-white/5 rounded-lg p-4">
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <Building2 className="text-[#D4AF37]" size={16} />
              Client Classification
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-white/40">Client Type</p>
                <ClientTypeBadge type={inquiry.client_type} />
              </div>
              <div>
                <p className="text-white/40">Referral Source</p>
                <p className="font-medium">{inquiry.referral_source || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Event Details */}
          <div className="bg-white/5 rounded-lg p-4">
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <Calendar className="text-[#D4AF37]" size={16} />
              Event Details
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-white/40">Event Type</p>
                <p className="font-medium">{inquiry.event_type || 'N/A'}</p>
              </div>
              <div>
                <p className="text-white/40">Event Date/Range</p>
                <p className="font-medium">{inquiry.event_date_or_range || 'N/A'}</p>
              </div>
              <div>
                <p className="text-white/40">Location</p>
                <p className="font-medium">{inquiry.event_city_location || 'N/A'}</p>
              </div>
              <div>
                <p className="text-white/40">Expected Guests</p>
                <p className="font-medium">{inquiry.expected_guest_count_range || 'N/A'}</p>
              </div>
              <div>
                <p className="text-white/40">Budget Range</p>
                <p className="font-medium text-[#D4AF37]">{inquiry.budget_range || 'N/A'}</p>
              </div>
              <div>
                <p className="text-white/40">Services Needed</p>
                <p className="font-medium">
                  {Array.isArray(inquiry.services_needed) 
                    ? inquiry.services_needed.join(', ') 
                    : inquiry.services_needed || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Inquiry Message */}
          <div className="bg-white/5 rounded-lg p-4">
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <MessageSquare className="text-[#D4AF37]" size={16} />
              Inquiry Message
            </h3>
            <p className="text-sm whitespace-pre-wrap">{inquiry.inquiry_message || 'No message provided.'}</p>
          </div>

          {/* Internal Notes (Admin Only) */}
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-4">
            <h3 className="font-medium mb-3 text-[#D4AF37]">Internal Notes (Admin Only)</h3>
            <Textarea
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              placeholder="Add internal notes about this inquiry..."
              className="bg-white/5 border-white/10 mb-3"
              rows={4}
            />
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="text-xs text-white/60 block mb-1">Assigned To</label>
                <Input
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  placeholder="Team member name"
                  className="bg-white/5 border-white/10"
                />
              </div>
              <Button
                onClick={handleSaveNotes}
                disabled={saving}
                className="bg-[#D4AF37] hover:bg-[#B8963A] text-black mt-5"
              >
                {saving ? 'Saving...' : 'Save Notes'}
              </Button>
            </div>
          </div>

          {/* Google Sheets Export Status (Admin visibility) */}
          {(inquiry.gsheets_export_status || inquiry.gsheets_export_error) && (
            <div className={`rounded-lg p-3 text-sm ${
              inquiry.gsheets_export_status === 'success' 
                ? 'bg-green-500/10 border border-green-500/30' 
                : 'bg-red-500/10 border border-red-500/30'
            }`}>
              <div className="flex items-center gap-2">
                {inquiry.gsheets_export_status === 'success' ? (
                  <>
                    <CheckCircle size={14} className="text-green-400" />
                    <span className="text-green-400">Exported to Google Sheets</span>
                  </>
                ) : (
                  <>
                    <AlertCircle size={14} className="text-red-400" />
                    <span className="text-red-400">Google Sheets export failed</span>
                  </>
                )}
              </div>
              {inquiry.gsheets_export_error && (
                <p className="text-xs text-red-400/70 mt-1">{inquiry.gsheets_export_error}</p>
              )}
            </div>
          )}

          {/* Metadata */}
          <div className="text-xs text-white/40 space-y-1">
            <p>Created: {new Date(inquiry.created_at).toLocaleString()}</p>
            {inquiry.read_at && <p>Read: {new Date(inquiry.read_at).toLocaleString()}</p>}
            {inquiry.updated_at && <p>Last Updated: {new Date(inquiry.updated_at).toLocaleString()}</p>}
            <p>Source: {inquiry.source || 'public_website'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Convert to Client Modal
const ConvertToClientModal = ({ inquiry, onClose, onSuccess }) => {
  const [createProject, setCreateProject] = useState(false);
  const [projectName, setProjectName] = useState(`Project for ${inquiry.full_name || 'Client'}`);
  const [converting, setConverting] = useState(false);
  const [result, setResult] = useState(null);

  const handleConvert = async () => {
    setConverting(true);
    try {
      const response = await api.convertInquiryToClient(
        inquiry.inquiry_id,
        createProject,
        projectName
      );
      setResult(response.data);
      toast.success('Client created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert');
    } finally {
      setConverting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4">
      <div className="bg-[#0A0A0F] border border-white/10 rounded-lg w-full max-w-md">
        <div className="p-6 border-b border-white/10">
          <h2 className="text-xl font-medium">Convert to Client</h2>
          <p className="text-sm text-white/60 mt-1">
            This will create a new client account for {inquiry.full_name}
          </p>
        </div>

        {!result ? (
          <div className="p-6 space-y-4">
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-sm"><strong>Name:</strong> {inquiry.full_name}</p>
              <p className="text-sm"><strong>Email:</strong> {inquiry.email}</p>
              <p className="text-sm"><strong>Company:</strong> {inquiry.company_organization || 'N/A'}</p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="createProject"
                checked={createProject}
                onChange={(e) => setCreateProject(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="createProject" className="text-sm">
                Also create a project for this client
              </label>
            </div>

            {createProject && (
              <div>
                <label className="text-sm text-white/60 block mb-1">Project Name</label>
                <Input
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  className="bg-white/5 border-white/10"
                />
              </div>
            )}

            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
              <p className="text-xs text-yellow-400">
                <AlertCircle size={14} className="inline mr-1" />
                A temporary password will be generated. You must securely share it with the client.
              </p>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose} className="flex-1 border-white/20">
                Cancel
              </Button>
              <Button
                onClick={handleConvert}
                disabled={converting}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {converting ? 'Converting...' : 'Convert to Client'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="text-center">
              <CheckCircle className="mx-auto text-green-400 mb-2" size={48} />
              <h3 className="text-lg font-medium">Client Created Successfully!</h3>
            </div>

            <div className="bg-white/5 rounded-lg p-4 space-y-2">
              <p className="text-sm"><strong>Client ID:</strong> {result.client_id}</p>
              <p className="text-sm"><strong>Email:</strong> {result.client_email}</p>
              {result.project_id && (
                <p className="text-sm"><strong>Project ID:</strong> {result.project_id}</p>
              )}
            </div>

            <div className="bg-[#D4AF37]/20 border border-[#D4AF37]/50 rounded-lg p-4">
              <p className="text-sm font-medium text-[#D4AF37] mb-1">Temporary Password:</p>
              <p className="font-mono text-lg bg-black/30 px-3 py-2 rounded select-all">
                {result.temporary_password}
              </p>
              <p className="text-xs text-white/60 mt-2">
                {result.note}
              </p>
            </div>

            <Button onClick={onSuccess} className="w-full bg-[#D4AF37] hover:bg-[#B8963A] text-black">
              Done
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInquiriesPage;
